import {
  createSlice,
  createSelector,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import { TeamsCapacityFilters } from 'types/store/teamsCapacity';
import { Status } from 'utils/customTypes';
import { SETTINGS_FILTERS, SLICE_STATUS } from 'utils/constants';
import {
  fetchFilterSettingByType,
  selectFiltersSettingsTypeId,
  updateFilterSetting,
  createFilterSetting,
} from 'state/Settings/Filters/FiltersSlice';
import { RootState, ThunkApi } from 'state/store';
import axios from 'axios';

interface InitialState {
  filters?: TeamsCapacityFilters;
  actionStatus: {
    fetch: {
      status: Status;
      error?: string;
    };
    upsert: {
      status: Status;
      error?: string;
    };
  };
}

const initialState: InitialState = {
  actionStatus: {
    fetch: {
      status: SLICE_STATUS.IDLE,
    },
    upsert: {
      status: SLICE_STATUS.IDLE,
    },
  },
};

export const upsertFiltersSettings = createAsyncThunk<
  TeamsCapacityFilters,
  { filters: TeamsCapacityFilters },
  ThunkApi
>(
  'capacities/filters/upsertFiltersSettings',
  async ({ filters }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const filtersSettingsTypeId = selectFiltersSettingsTypeId(
        state,
        SETTINGS_FILTERS.TEAMS_CAPACITY
      );
      if (filtersSettingsTypeId) {
        await dispatch(
          updateFilterSetting({
            id: filtersSettingsTypeId,
            updateFields: {
              filter_settings: filters,
            },
          })
        ).unwrap();
      } else {
        await dispatch(
          createFilterSetting({
            filter_type: SETTINGS_FILTERS.TEAMS_CAPACITY,
            filter_settings: filters,
          })
        ).unwrap();
      }
      return filters;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

const filtersSlice = createSlice({
  name: 'weeklyTeamCapacities/filters',
  initialState,
  reducers: {
    resetCapacityFiltersState: (state) => {
      return initialState;
    },
    setCapacityFilters: (
      state,
      action: PayloadAction<TeamsCapacityFilters>
    ) => {
      state.filters = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterSettingByType.pending, (state, action) => {
        if (action.meta.arg === SETTINGS_FILTERS.TEAMS_CAPACITY) {
          state.actionStatus.fetch.status = SLICE_STATUS.LOADING;
        }
      })
      .addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
        if (action.meta.arg !== SETTINGS_FILTERS.TEAMS_CAPACITY) {
          return;
        }
        if (!action.payload) {
          state.actionStatus.fetch.status = SLICE_STATUS.SUCCESS;
          return;
        }
        const { filter_settings } = action.payload;
        state.filters = filter_settings;
        state.actionStatus.fetch.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(fetchFilterSettingByType.rejected, (state, action) => {
        if (action.meta.arg === SETTINGS_FILTERS.TEAMS_CAPACITY) {
          state.actionStatus.fetch.status = SLICE_STATUS.FAILED;
        }
      })
      .addCase(upsertFiltersSettings.pending, (state) => {
        state.actionStatus.upsert.status = SLICE_STATUS.LOADING;
      })
      .addCase(upsertFiltersSettings.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }
        state.filters = {
          ...state.filters,
          ...action.payload,
        };
        state.actionStatus.upsert.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(upsertFiltersSettings.rejected, (state, action) => {
        state.actionStatus.upsert.status = SLICE_STATUS.FAILED;
        state.actionStatus.upsert.error = action.payload as string;
      });
  },
});

export const { resetCapacityFiltersState, setCapacityFilters } =
  filtersSlice.actions;

const selectTeamsCapacityFiltersState = (state: RootState) =>
  state.capacities.filters;

export const selectShouldFetchCapacityFilters = createSelector(
  [selectTeamsCapacityFiltersState],
  (state: InitialState) => {
    const FETCHED_STATUS: string[] = [
      SLICE_STATUS.SUCCESS,
      SLICE_STATUS.FAILED,
    ];
    return !FETCHED_STATUS.includes(state.actionStatus.fetch.status);
  }
);

export const selectCapacityFilters = createSelector(
  [selectTeamsCapacityFiltersState],
  (state: InitialState) => state.filters
);

export default filtersSlice.reducer;
